import * as React from "react";
import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui";
import { RouteBlocker } from "../useFormRouteBuilder";
import { RouteStatus, RouteUrl, useCopyRoute } from "./RouteOutput";

/**
 * True once `element` has scrolled out of view through the top. Takes the element, not a ref, so
 * it starts observing when the card mounts after loading. Stays false without IntersectionObserver.
 */
export function useScrolledPast(element: Element | null) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      // Only "past" when it left through the top, not while it's still below the fold
      setPast(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return past;
}

interface Props {
  visible: boolean;
  url: string | null;
  blocker: RouteBlocker | null;
  statusText: string;
  onFix: (blocker: RouteBlocker) => void;
}

/**
 * The route docked to the top of the page once its card scrolls away, so every change in the steps
 * below shows up without scrolling back. Full width and flush to the top so it reads as page chrome,
 * not another card; its contents line up with the column. A zero-height sticky slot, so it never
 * shifts the page.
 */
export function StickyRouteBar({ visible, url, blocker, statusText, onFix }: Props) {
  const { copied, copyRoute } = useCopyRoute(url);
  return (
    // -top-4 cancels the Reputation tab's p-4: browsers pin sticky elements inside the scroll padding
    <div className="sticky -top-4 z-20 h-0">
      {visible && (
        <div
          data-testid="sticky-route-bar"
          className="absolute inset-x-0 top-0 border-b-2 border-primary/40 bg-white shadow-[0_10px_24px_-12px_rgba(15,23,42,0.35)]"
        >
          <div className="max-w-[960px] mx-auto px-6 py-3 flex items-start gap-4">
            <div className="min-w-0 flex-1">{url && <RouteUrl url={url} variant="bar" />}</div>
            {/* Status over the button keeps the URL's lines as long as possible */}
            <div className="shrink-0 max-w-[14rem] flex flex-col items-end gap-1.5 text-right">
              <RouteStatus blocker={blocker} text={statusText} onFix={onFix} className="text-xs" />
              <Button size="sm" onClick={copyRoute} disabled={!!blocker || !url}>
                {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy route"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
